import type { ExecuteWorkflowNodeRequest } from '@unifyapps/network/generated/models/executeWorkflowNodeRequest';
import _get from 'lodash/get';
import get from 'lodash/get';
import omit from 'lodash/omit';
import _set from 'lodash/set';
import { TRANSFORMED_RESPONSE_KEY } from '../../../consts';
import type { InfinitePaginationInfo } from '../types';

/**
 * Parameters required to initialize pagination
 */
interface GetInitialPageParams {
  /** Starting position for data retrieval */
  offset: number;
  /** Number of items to retrieve per page */
  limit: number;
  /** Pagination configuration */
  infinitePaginationInfo: InfinitePaginationInfo;
  /** Pagination strategy (offset-based or cursor-based) */
  paginateBy?: 'OFFSET' | 'CURSOR';
}

/** Default starting position when none is specified */
const DEFAULT_OFFSET = 0;
/** Default number of items per page when none is specified */
const DEFAULT_PAGE_SIZE = 20;

/**
 * Extracts pagination parameters from a workflow node execution request
 *
 * This function retrieves pagination values (offset, limit) from a datasource request
 * based on the paths defined in the pagination configuration.
 *
 * @param payload - The workflow node request payload to extract pagination values from
 * @param infinitePaginationInfo - Configuration specifying where pagination values are located
 * @returns Object containing pagination values and cleaned payload
 */
export const getInfiniteDataSourceWorkflowNodePageParams = (
  payload: ExecuteWorkflowNodeRequest,
  infinitePaginationInfo: InfinitePaginationInfo,
) => {
  const { offsetPath, limitPath, paginateByPath } = infinitePaginationInfo;

  // Extract pagination values from payload using the configured paths
  const offset = (_get(payload, offsetPath) as unknown) ?? DEFAULT_OFFSET;
  const limit = (_get(payload, limitPath) as unknown) ?? DEFAULT_PAGE_SIZE;

  // Warn if pagination values aren't numbers
  if (typeof offset !== 'number' || typeof limit !== 'number') {
    console.warn('limit and offset must be numbers in datasource request');
  }

  return {
    offset: offset as number,
    limit: limit as number,
    // Remove offset from payload (will be added back when appropriate)
    payload: omit(payload, [offsetPath]) as ExecuteWorkflowNodeRequest,
    // Extract pagination strategy if configured
    paginateBy: paginateByPath ? (_get(payload, paginateByPath) as 'OFFSET' | 'CURSOR') : undefined,
  };
};

/**
 * Creates an object with pagination parameters for initial data loading
 *
 * This function builds the pagination parameter object used for the first
 * page of data according to the pagination configuration.
 *
 * @param data - Information needed to build initial pagination parameters
 * @returns An object containing pagination parameters at their correct paths
 */
export const getInitialPageParams = (data: GetInitialPageParams) => {
  const { offset, limit, infinitePaginationInfo, paginateBy } = data;
  const pageParams: Record<string, unknown> = {};

  // Set pagination values at the appropriate paths in the resulting object
  _set(pageParams, infinitePaginationInfo.offsetPath, offset);
  _set(pageParams, infinitePaginationInfo.limitPath, limit);

  // Set pagination strategy if configured
  if (infinitePaginationInfo.paginateByPath) {
    _set(pageParams, infinitePaginationInfo.paginateByPath, paginateBy);
  }

  return pageParams;
};

/**
 * Creates pagination parameters for the next page of data
 *
 * This function calculates and builds the pagination parameters needed
 * to retrieve the next set of data in an infinite pagination sequence.
 *
 * @param payload - The current workflow node request payload
 * @param infinitePaginationInfo - Pagination configuration
 * @param paginateBy - Optional pagination strategy
 * @returns An object containing pagination parameters for the next page
 */
export const getNextPageParams = (
  payload: ExecuteWorkflowNodeRequest,
  infinitePaginationInfo: InfinitePaginationInfo,
  paginateBy?: 'OFFSET' | 'CURSOR',
) => {
  // Extract current pagination values
  const { offset, limit } = getInfiniteDataSourceWorkflowNodePageParams(
    payload,
    infinitePaginationInfo,
  );

  // Create parameters for the next page by incrementing the offset
  return getInitialPageParams({
    offset: offset + limit,
    limit,
    infinitePaginationInfo,
    paginateBy,
  });
};

/**
 * Determines if there are more pages of data to load
 *
 * This function checks if additional data is available by examining
 * the response from the last page request against the pagination configuration.
 *
 * @param lastPage - The response from the most recent page request
 * @param lastPageParam - The pagination parameters used for the last request
 * @param infinitePaginationInfo - Pagination configuration
 * @returns Boolean indicating if more data is available
 */
export const getHasNextPage = ({
  lastPage,
  lastPageParam,
  infinitePaginationInfo,
  isTransformerConfigured,
}: {
  lastPage: { response?: unknown } | undefined;
  lastPageParam: Record<string, unknown>;
  infinitePaginationInfo: InfinitePaginationInfo;
  isTransformerConfigured: boolean;
}) => {
  const response = isTransformerConfigured
    ? get(lastPage?.response, TRANSFORMED_RESPONSE_KEY)
    : lastPage?.response;
  const { totalCountPath, hasMorePath } = infinitePaginationInfo;

  // If a direct "has more" indicator is available, use it
  if (hasMorePath) {
    return Boolean(_get(response, hasMorePath));
  }

  // Otherwise, calculate based on total count, offset, and limit
  if (totalCountPath) {
    const offset = _get(lastPageParam, infinitePaginationInfo.offsetPath) as number | undefined;
    const limit = _get(lastPageParam, infinitePaginationInfo.limitPath) as number | undefined;
    const total = _get(response, totalCountPath) as number | undefined;

    // Return false if any required values are missing or not numbers
    if (typeof offset !== 'number' || typeof limit !== 'number' || typeof total !== 'number') {
      return false;
    }

    return total > offset + limit;
  }

  // Default to no more pages if neither method can determine
  return false;
};
